import { App, TierartEnum } from '@ps20-frontend/frontend-models';
import { ProduktDeleted } from '@ps20-frontend/shop-integration-redux';
import { childKeyOf } from './childKeys';
import { createTier } from './tier';
import { THH_TIERART_OF } from './tierPositions';
import { TgvTier, TiergruppeState } from './tiergruppe.model';

const uncheckedTkvCardIds = (
  tiergruppe: TiergruppeState,
  uncheckedKeys: Set<string>,
  takenOver: Set<string>,
): Set<string> =>
  new Set(
    Object.entries(tiergruppe.angebotsIds)
      .filter(([cardId, id]) => uncheckedKeys.has(id) && takenOver.has(cardId))
      .map(([cardId]) => cardId),
  );

const uncheckedThhTierarten = (
  uncheckedKeys: Set<string>,
  takenOver: Set<string>,
): Set<TierartEnum | null> =>
  new Set<TierartEnum | null>(
    [...uncheckedKeys]
      .filter(key => takenOver.has(key))
      .flatMap(key => {
        const tierart = THH_TIERART_OF[key as keyof typeof THH_TIERART_OF];
        return tierart === undefined ? [] : [tierart];
      }),
  );

const withoutEmptiedCards = (before: TgvTier[], after: TgvTier[]): TgvTier[] =>
  after.filter((tier, index) => {
    const { tkv, thh } = before[index].umfang;
    return !((tkv || thh) && !tier.umfang.tkv && !tier.umfang.thh);
  });

const withoutCoverage = (
  animals: TgvTier[],
  tkvCardIds: Set<string>,
  thhTierarten: Set<TierartEnum | null>,
): TgvTier[] => {
  const isUnchecked = (tier: TgvTier): boolean =>
    (tier.umfang.tkv && tkvCardIds.has(tier.cardId)) ||
    (tier.umfang.thh && thhTierarten.has(tier.tierart));
  if (!animals.some(isUnchecked)) {
    return animals;
  }
  const reduced = animals.map(tier => ({
    ...tier,
    umfang: {
      tkv: tier.umfang.tkv && !tkvCardIds.has(tier.cardId),
      thh: tier.umfang.thh && !thhTierarten.has(tier.tierart),
    },
  }));
  const remaining = withoutEmptiedCards(animals, reduced);
  return remaining.length > 0 ? remaining : [createTier(animals[0].cardId)];
};

export const tiereWithoutDeletedOffers = (
  tiergruppe: TiergruppeState,
  deleted: ProduktDeleted['angebotsIds'],
  skippedKeys: string[],
): TgvTier[] => {
  const takenOver = new Set(tiergruppe.uebernommen ?? []);
  const uncheckedKeys = new Set(
    deleted
      .map(childKeyOf)
      .filter((key): key is string => key !== null && !skippedKeys.includes(key)),
  );
  return withoutCoverage(
    tiergruppe.tiere,
    uncheckedTkvCardIds(tiergruppe, uncheckedKeys, takenOver),
    uncheckedThhTierarten(uncheckedKeys, takenOver),
  );
};

const deletedTkvCardIds = (
  snapshot: TiergruppeState,
  deletedKeys: ReadonlySet<string>,
): Set<string> =>
  new Set(
    Object.entries(snapshot.angebotsIds)
      .filter(([, angebotsId]) => deletedKeys.has(angebotsId))
      .map(([cardId]) => cardId),
  );

const deletedThhTierarten = (
  snapshot: TiergruppeState,
  deletedKeys: ReadonlySet<string>,
): Set<TierartEnum | null> =>
  new Set<TierartEnum | null>([
    ...(snapshot.thhAngelegt.thhHund && deletedKeys.has(App.THH_HUND) ? [TierartEnum.HUND] : []),
    ...(snapshot.thhAngelegt.thhPferd && deletedKeys.has(App.THH_PFERD) ? [TierartEnum.PFERD] : []),
  ]);

export const animalsWithoutDeletedChildren = (
  snapshot: TiergruppeState,
  deletedKeys: ReadonlySet<string>,
): TgvTier[] =>
  withoutCoverage(
    snapshot.tiere,
    deletedTkvCardIds(snapshot, deletedKeys),
    deletedThhTierarten(snapshot, deletedKeys),
  );
